// frontend/shared/src/dc.ts —— TD-804 / TD-806 / TD-826：DC 宿主组合器（各域 state / i18n / 生命周期 / renderVals 合并）与事件总线
export type Lang = 'zh' | 'en';
export type Dict = Readonly<Record<string, unknown>>;
export interface I18nPack {
  readonly zh: Dict;
  readonly en: Dict;
}

export interface DcHost<S> {
  state: S;
  readonly props: Readonly<Record<string, unknown>>;
  setState(update: Partial<S> | ((s: S) => Partial<S>), cb?: () => void): void;
}

export interface GpEvents {
  'session:ready': { reason: 'boot' | 'login' };
  'session:lost': { reason: string };
  screen: { from: string; to: string };
  profile: Record<string, never>;
  'home:ready': Record<string, never>;
}

export interface EventBus {
  on<K extends keyof GpEvents>(k: K, fn: (p: GpEvents[K]) => void): () => void;
  emit<K extends keyof GpEvents>(k: K, p: GpEvents[K]): void;
}

export interface DomainCtx<S, G> {
  host: DcHost<S>;
  s: S;
  t: Dict;
  lang: Lang;
  gp: G;
  events: EventBus;
}

export interface Domain<S, G> {
  readonly name: string;
  state?(props: Readonly<Record<string, unknown>>): Partial<S>;
  readonly i18n?: I18nPack;
  readonly rewrites?: readonly string[];
  mount?(ctx: DomainCtx<S, G>): void;
  update?(ctx: DomainCtx<S, G>, prev: S): void;
  unmount?(ctx: DomainCtx<S, G>): void;
  vals(ctx: DomainCtx<S, G>): Record<string, unknown>;
}

export interface GpApp<S> {
  initialState(props: Readonly<Record<string, unknown>>, protoDict: Readonly<Record<Lang, Dict>>): S;
  t(host: DcHost<S>): Dict;
  didMount(host: DcHost<S>): void;
  didUpdate(host: DcHost<S>, prevProps: Readonly<Record<string, unknown>>): void;
  willUnmount(host: DcHost<S>): void;
  renderVals(host: DcHost<S>): Record<string, unknown>;
  readonly events: EventBus;
}

type Listeners = { [K in keyof GpEvents]: Set<(p: GpEvents[K]) => void> };

// 单个监听器抛错只记日志，不中断其余监听器；emit 期间增删监听器不影响本轮（遍历快照）
export function createEventBus(): EventBus {
  const subs: Listeners = { 'session:ready': new Set(), 'session:lost': new Set(), screen: new Set(), profile: new Set(), 'home:ready': new Set() };
  return {
    on(k, fn) {
      subs[k].add(fn);
      return () => {
        subs[k].delete(fn);
      };
    },
    emit(k, p) {
      for (const fn of [...subs[k]]) {
        try {
          fn(p);
        } catch (e) {
          console.error('[gp:event]', k, e);
        }
      }
    },
  };
}

// base 只含各端首任务定义的公共字段（UiState + 外壳切片）；其余域切片由各域 state() 提供初值。
// S 的完整性由「每个以 declare module 增补切片的域必须实现 state() 返回本切片初值」保证，
// 各端 keys 用例（h5-keys / agent-keys / admin-keys）断言 initialState 结果含全部切片键。
export type BaseState<S extends { lang: Lang }> = Pick<S, 'lang'> & Partial<S>;

export interface CreateOptions<S extends { lang: Lang }, G> {
  base: BaseState<S>;
  domains: ReadonlyArray<Domain<S, G>>;
  services: (events: EventBus) => G;
  dev?: boolean;
  langOf?: (s: S) => Lang;
}

export function createDcApp<S extends { lang: Lang }, G>(o: CreateOptions<S, G>): GpApp<S> {
  const events = createEventBus();
  const gp = o.services(events);
  const langOf = o.langOf ?? ((s: S) => s.lang);
  let proto: Readonly<Record<Lang, Dict>> = { zh: {}, en: {} };
  const dictCache = new Map<Lang, Dict>();
  const prevState = new WeakMap<object, S>();
  let reported = false;

  // 字典优先级（TD-805）：域字典 < 原型字典 < 域 rewrites 显式覆盖的键
  const dict = (lang: Lang): Dict => {
    let d = dictCache.get(lang);
    if (d) return d;
    const extra: Record<string, unknown> = {};
    const forced: Record<string, unknown> = {};
    for (const dom of o.domains) {
      const pack = dom.i18n?.[lang] ?? {};
      Object.assign(extra, pack);
      for (const k of dom.rewrites ?? []) if (k in pack) forced[k] = pack[k];
    }
    d = { ...extra, ...proto[lang], ...forced };
    dictCache.set(lang, d);
    return d;
  };

  const ctx = (host: DcHost<S>): DomainCtx<S, G> => {
    const lang = langOf(host.state);
    return { host, s: host.state, t: dict(lang), lang, gp, events };
  };

  return {
    events,
    initialState(props, protoDict) {
      proto = protoDict;
      dictCache.clear();
      let merged: Partial<S> = { ...o.base };
      for (const dom of o.domains) merged = { ...merged, ...(dom.state?.(props) ?? {}) };
      // 唯一的组合断言点：切片完整性由各域 state() 与 keys 用例保证（见 BaseState 注释）
      return merged as S;
    },
    t: (host) => dict(langOf(host.state)),
    didMount(host) {
      prevState.set(host, host.state);
      for (const dom of o.domains) dom.mount?.(ctx(host));
    },
    didUpdate(host) {
      const prev = prevState.get(host) ?? host.state;
      prevState.set(host, host.state);
      if (prev === host.state) return;
      for (const dom of o.domains) dom.update?.(ctx(host), prev);
    },
    willUnmount(host) {
      for (const dom of o.domains) dom.unmount?.(ctx(host));
      prevState.delete(host);
    },
    // TD-826：各域 vals 键不得重叠；dev 抛错，生产只报一次并以后注册的域为准
    renderVals(host) {
      const c = ctx(host);
      const out: Record<string, unknown> = {};
      const owner = new Map<string, string>();
      for (const dom of o.domains) {
        const v = dom.vals(c);
        for (const k of Object.keys(v)) {
          const prevOwner = owner.get(k);
          if (prevOwner !== undefined) {
            const msg = `renderVals 键冲突：${k}（${prevOwner} / ${dom.name}）`;
            if (o.dev) throw new Error(msg);
            if (!reported) {
              reported = true;
              console.error('[gp:dc]', msg);
            }
          }
          owner.set(k, dom.name);
          out[k] = v[k];
        }
      }
      return out;
    },
  };
}
